import { useEffect, useCallback } from 'react';
import { Video } from '../types';
import { ChannelInfo, ChannelTotals } from '../hooks/useChannel';
import { formatViewCount } from '../utils/format';
import { type Lang, DEFAULT_LANG, getUi } from '../../functions/lib/i18n';

interface ChannelModalProps {
  channelId: string | null;
  channel: ChannelInfo | null;
  /** Best-known title, even when the live API call failed (falls back to D1). */
  title: string | null;
  totals: ChannelTotals | null;
  /** This channel's videos we have ever tracked, ranked by peak views (D1). */
  trackedVideos?: Video[];
  isLoading: boolean;
  error: string | null;
  /** Videos currently on screen — used as a fallback if D1 has no history yet. */
  allVideos?: Video[];
  onClose: () => void;
  onPlayVideo?: (video: Video) => void;
  onOpenChannelPage?: (channelId: string) => void;
  lang?: Lang;
}

/**
 * Panel with public info about a channel (avatar, subscriber/video/view counts,
 * description) plus its most-viewed tracked videos. Live metadata and the
 * tracked-video list are independent — the panel still renders from D1 alone
 * if the live YouTube lookup fails.
 */
export function ChannelModal({
  channelId,
  channel,
  title,
  totals,
  trackedVideos = [],
  isLoading,
  error,
  allVideos = [],
  onClose,
  onPlayVideo,
  onOpenChannelPage,
  lang = DEFAULT_LANG,
}: ChannelModalProps) {
  const ui = getUi(lang);
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (channelId) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [channelId, handleKeyDown]);

  if (!channelId) return null;

  const channelUrl = channel?.customUrl
    ? `https://www.youtube.com/${channel.customUrl}`
    : `https://www.youtube.com/channel/${channelId}`;

  // Prefer our own tracked history (ranked by peak views); fall back to
  // whatever's already loaded on the page if D1 has nothing for this channel.
  const videoList = trackedVideos.length > 0
    ? trackedVideos
    : allVideos.filter((v) => v.channelId === channelId);
  const videoListLabel = trackedVideos.length > 0 ? ui.channelTrackedVideos(videoList.length) : ui.channelOnChart(videoList.length);

  const displayTitle = channel?.title || title;

  return (
    <div className="channel-modal-overlay" onClick={onClose}>
      <div className="channel-modal" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal-close" onClick={onClose} title="Close (Esc)">
          ✕
        </button>

        {isLoading && <div className="channel-modal-status">{ui.channelLoading}</div>}
        {error && <div className="channel-modal-status">{error}</div>}

        {displayTitle && !isLoading && !error && (
          <>
            {channel?.bannerUrl && (
              <div className="channel-banner">
                <img src={`${channel.bannerUrl}=w1060`} alt="" loading="lazy" />
              </div>
            )}

            <div className="channel-header">
              {channel?.thumbnailUrl && (
                <img className="channel-avatar" src={channel.thumbnailUrl} alt={displayTitle} />
              )}
              <div className="channel-heading">
                <h3 className="channel-title">{displayTitle}</h3>
                {channel?.customUrl && <p className="channel-handle">{channel.customUrl}</p>}
                <div className="channel-stats">
                  {channel && channel.subscriberCount !== null && (
                    <span>{formatViewCount(channel.subscriberCount)} {ui.channelSubscribers}</span>
                  )}
                  <span>{formatViewCount(channel?.videoCount ?? totals?.videoCount ?? 0)} {ui.channelVideosLabel}</span>
                  <span>{formatViewCount(channel?.viewCount ?? totals?.totalViews ?? 0)} {ui.channelTotalViews}</span>
                </div>
              </div>
            </div>

            {channel?.description && (
              <p className="channel-description">{channel.description}</p>
            )}

            <div className="channel-modal-actions">
              <a className="channel-visit-btn" href={channelUrl} target="_blank" rel="noopener noreferrer">
                {ui.channelVisit} ↗
              </a>
              <a
                className="channel-visit-btn"
                href={`/channel/${channelId}`}
                onClick={(e) => {
                  if (!onOpenChannelPage || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  onOpenChannelPage(channelId);
                }}
              >
                {ui.channelFullPage} →
              </a>
            </div>

            {videoList.length > 0 && (
              <div className="channel-videos">
                <h4 className="channel-videos-title">{videoListLabel}</h4>
                <div className="channel-videos-list">
                  {videoList.map((v) => (
                    <button
                      key={v.id}
                      className="channel-video-item"
                      onClick={() => onPlayVideo?.(v)}
                    >
                      <img src={v.thumbnail} alt="" className="channel-video-thumb" />
                      <div className="channel-video-info">
                        <div className="channel-video-title">{v.title}</div>
                        <div className="channel-video-views">
                          {formatViewCount(v.viewCount)} views
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
